import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';

import { Icon, IconMagnifier } from '@flixbus/honeycomb-icons-react';
import {
	Box,
	Button,
	Divider,
	Heading,
	Panel,
	PanelContent,
	PanelHeader,
	Popup,
	PopupSection,
	Text,
} from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import { formatOverlappingActivities } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';
import CrewMemberFilter from 'pages/RideDetails/components/CrewMemberSearch';
import DriverCard from 'pages/RideDetails/components/DriverCard';
import { rideDetailsStore } from 'pages/RideDetails/RideDetailsStore';

import { driversWidgetStore as store } from './DriverWidgetStore';
import { driversWidgetText } from './text';

import styles from './styles.module.css';

const DriversWidget = observer(() => {
	const { isInDriverSelection, workAssignmentInSelection, rideId, activityType, selectedTimezone } =
		rideDetailsStore;
	const workAssignment = rideDetailsStore.getAssignmentByID(workAssignmentInSelection);
	const { fromStationId, toStationId } = workAssignment;
	const toStation = rideDetailsStore.getStationById(toStationId);
	const fromStation = rideDetailsStore.getStationById(fromStationId);
	const isListReady = !store.isSearchLoading && !store.loadingEligible && !store.errorEligible;

	const eligibleTo = useMemo(
		() => ({ rideId, fromStationId, toStationId, activityType }),
		[rideId, fromStationId, toStationId, activityType]
	);

	useEffect(() => {
		store.setEligibleTo(eligibleTo);
	}, [eligibleTo]);

	useEffect(() => {
		if (!eligibleTo.fromStationId || !eligibleTo.toStationId) return;

		if (rideDetailsStore.reassignCrewMemberId) return;

		store.fetchEligible(eligibleTo);
	}, [eligibleTo]);

	useEffect(() => {
		if (!rideDetailsStore.reassignCrewMemberId || !isInDriverSelection) return;

		const crewMember = rideDetailsStore.reassignCrewMemberId;

		rideDetailsStore.clearReassignCrewMemberId();
		store.setReassignMode(true);
		store.validateSelection({ crewMember }, eligibleTo);
	}, [isInDriverSelection, eligibleTo]);

	return (
		<>
			{!store.isReassignMode && (
				<Panel
					hasOverlay
					id="drivers-widget"
					active={isInDriverSelection}
					onOverlayClick={() => store.close()}
					extraClasses={styles.width}
					position="default"
				>
					<PanelHeader closeButtonProps={{ label: 'Close', onClick: () => store.close() }}>
						{driversWidgetText.panelHeader}
					</PanelHeader>
					<PanelContent extraClasses={styles.width}>
						<CrewMemberFilter
							isInSearchMode={store.showFiltered}
							closeSearchMode={() => store.closeSearch()}
							startSearchMode={() => store.startSearch()}
							updateExternalData={(list) => store.setFiltered(list)}
							setLoading={(v) => store.setSearchLoading(v)}
							eligibleTo={eligibleTo}
						/>

						<div className={styles.scroll}>
							{store.showFiltered ? (
								<>
									<Box extraClasses={styles.info}>
										<Icon InlineIcon={IconMagnifier} extraClasses={styles.icon} size={5} />
										{driversWidgetText.searchInfo}
									</Box>
									{store.isSearchLoading && <Loader />}
								</>
							) : (
								<>
									<h3 className={styles.perfectFit}>{driversWidgetText.perfectFit}</h3>
									{store.loadingEligible && <Loader />}
									{store.errorEligible && !store.loadingEligible && (
										<div className={styles.bold}>{driversWidgetText.errorMessage}</div>
									)}
								</>
							)}

							{store.optionsDrivers.length > 0 ? (
								<>
									{store.optionsDrivers.map((driver) => {
										const isDriverBeingAssigned =
											store.loadingValidation &&
											store.selectedDriver?.crewMember?.id === driver?.crewMember?.id;
										const issues = store.eligibilityIssuesByDriverId[driver?.crewMember?.id];

										return (
											<div className={styles.item} key={driver.id}>
												<DriverCard
													onAssign={() => store.validateSelection(driver, eligibleTo)}
													onExpand={() => store.validateSingleDriver(driver, eligibleTo)}
													driver={driver}
													isInAssignState={isDriverBeingAssigned}
													isAssignDisabled={store.loadingValidation}
													elegibilityIssues={issues}
												/>
											</div>
										);
									})}
								</>
							) : (
								isListReady && (
									<div className={styles.bold}>
										{store.showFiltered
											? driversWidgetText.noCrewMembersFound
											: driversWidgetText.noEligibleCrewMembers}
									</div>
								)
							)}
						</div>
					</PanelContent>
				</Panel>
			)}

			<Popup extraClasses={styles.popup} active={store.showsReasons}>
				<PopupSection type="title">
					<Heading
						id="accessible-popup-title"
						size={2}
						sectionHeader
						flushSpace
						extraClasses={styles.warningTitle}
					>
						{driversWidgetText.warningTitle}
					</Heading>
					<Text Elem="strong" extraClasses={styles.bold}>
						{`${store.selectedDriver?.crewMember?.fullName ?? ''}: ${fromStation?.name ?? ''} → ${toStation?.name ?? ''}`}
					</Text>
				</PopupSection>
				<PopupSection type="content">
					<Text extraClasses={styles.leftText}>{driversWidgetText.warningDescription}</Text>
					{store.reasons.length > 0 && (
						<ul className={styles.reasonsList}>
							{store.reasons.map((reason, index) => (
								<li className={styles.reasonItem} key={index.toString()}>
									<Text Elem="strong" extraClasses={styles.bold}>
										{reason.defaultMessage}
									</Text>
								</li>
							))}
						</ul>
					)}
					<Text extraClasses={styles.leftText}>{driversWidgetText.warningDisclaimer}</Text>
					{store.overlaps.length > 0 && (
						<div className={styles.driversOverlappingBanner}>
							<Divider extraClasses={styles.driversDivider} />
							<OverlappingBanner
								overlappingList={formatOverlappingActivities(
									store.overlaps,
									selectedTimezone?.value
								)}
								overlappingAction={store.overlappingAction}
								onOverlappingActionChange={(value) => store.setOverlappingAction(value)}
								cannotCancelOverlaps={store.cannotCancelOverlaps}
								isDelayOptionAvailable={store.isDelayOptionAvailable}
								delayMinutes={store.delayMinutes}
								onDelayMinutesChange={(value) => store.setDelayMinutes(value)}
								delayError={store.delayError}
								minDelayMinutes={store.minDelayMinutes}
							/>
						</div>
					)}
				</PopupSection>
				<PopupSection type="actions">
					<Button onClick={() => (store.isReassignMode ? store.close() : store.hideReasons())}>
						{driversWidgetText.cancelButton}
					</Button>
					<Button
						disabled={store.isAssignmentDisabled}
						appearance="danger"
						onClick={() => store.assignAnyway()}
					>
						{driversWidgetText.assignAnyway}
					</Button>
				</PopupSection>
			</Popup>
		</>
	);
});

export default DriversWidget;
